import type { StorageAdapter } from '../core/adapters';
import type { MarkConfig, StorageData } from '../types';

const CLICK_PARAM_CANDIDATES = [
  'click_id',
  'ch_click_id',
  'gclid',
  'gbraid',
  'wbraid',
  'dclid',
  'msclkid',
  'fbclid',
  'ttclid',
  'twclid',
  'li_fat_id',
];
const CAMPAIGN_PARAM_CANDIDATES = ['cid', 'campaign_id'];

const DEFAULT_CAPTURE_QUERY_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'ref',
  'referral',
  'affiliate_id',
  'click_id',
  'ch_click_id',
  'cid',
  'campaign_id',
  'gclid',
  'gbraid',
  'wbraid',
  'dclid',
  'msclkid',
  'fbclid',
  'ttclid',
  'twclid',
  'li_fat_id',
] as const;

const QUERY_KEY_ALIASES: Record<string, string> = {
  referral: 'ref',
  affiliate_id: 'ref',
  ch_click_id: 'click_id',
  cid: 'campaign_id',
};

const DEFAULT_DENYLIST = ['email', 'phone', 'token', 'auth', 'password', 'code'] as const;
const DEFAULT_MAX_CAPTURED_QUERY_PARAMS = 30;
const DEFAULT_MAX_QUERY_PARAM_VALUE_LENGTH = 256;

type AttributionCaptureConfig = Pick<
  MarkConfig,
  | 'capture_query_params'
  | 'capture_all_query_params'
  | 'query_param_denylist'
  | 'max_captured_query_params'
  | 'max_query_param_value_length'
>;

export function captureAttributionFromUrl(
  storage: StorageAdapter,
  config: AttributionCaptureConfig = {}
) {
  if (typeof window === 'undefined') {
    return;
  }

  const updates = extractAttributionFromSearch(window.location.search, config);
  if (Object.keys(updates).length > 0) {
    storage.update(updates);
  }
}

export function extractAttributionFromSearch(
  search: string,
  config: AttributionCaptureConfig = {}
): Partial<StorageData> {
  const params = new URLSearchParams(search);
  const normalizedParamMap = buildNormalizedParamMap(params);
  const clickId = findFirst(normalizedParamMap, CLICK_PARAM_CANDIDATES);
  const campaignId = findFirst(normalizedParamMap, CAMPAIGN_PARAM_CANDIDATES);
  const queryParams = captureQueryParams(params, config);

  const updates: Partial<StorageData> = {};
  if (clickId) updates.last_click_id = clickId;
  if (campaignId) updates.campaign_id = campaignId;
  if (Object.keys(queryParams).length > 0) updates.query_params = queryParams;

  return updates;
}

function captureQueryParams(
  params: URLSearchParams,
  config: AttributionCaptureConfig
): Record<string, string> {
  const queryParams: Record<string, string> = {};
  const denylist = new Set(getNormalizedKeys(config.query_param_denylist, DEFAULT_DENYLIST));
  const maxParams = normalizeNonNegativeInteger(
    config.max_captured_query_params,
    DEFAULT_MAX_CAPTURED_QUERY_PARAMS
  );
  const maxValueLength = normalizeNonNegativeInteger(
    config.max_query_param_value_length,
    DEFAULT_MAX_QUERY_PARAM_VALUE_LENGTH
  );
  const captureAll = config.capture_all_query_params ?? false;

  const allowedKeys = captureAll
    ? null
    : new Set([
        ...DEFAULT_CAPTURE_QUERY_KEYS.map(normalizeKey),
        ...getNormalizedKeys(config.capture_query_params, []),
      ]);

  for (const [rawKey, rawValue] of params.entries()) {
    const key = normalizeKey(rawKey);
    if (!key) {
      continue;
    }
    const canonicalKey = QUERY_KEY_ALIASES[key] ?? key;
    if (denylist.has(key) || denylist.has(canonicalKey)) {
      continue;
    }

    if (allowedKeys && !allowedKeys.has(key)) {
      continue;
    }
    const value = rawValue.trim();
    if (!value) {
      continue;
    }

    if (!(canonicalKey in queryParams) && Object.keys(queryParams).length >= maxParams) {
      break;
    }

    queryParams[canonicalKey] = value.slice(0, maxValueLength);
  }

  return queryParams;
}

function findFirst(params: Record<string, string>, candidates: readonly string[]) {
  for (const key of candidates) {
    const normalizedCandidate = normalizeKey(key);
    const value = params[normalizedCandidate]?.trim();
    if (value) {
      return value;
    }
  }
  return undefined;
}

function buildNormalizedParamMap(params: URLSearchParams) {
  const map: Record<string, string> = {};
  for (const [rawKey, rawValue] of params.entries()) {
    const key = normalizeKey(rawKey);
    if (!key || key in map) {
      continue;
    }
    map[key] = rawValue;
  }
  return map;
}

function normalizeKey(key: string) {
  return key.trim().toLowerCase();
}

function getNormalizedKeys(
  keys: readonly string[] | undefined,
  defaults: readonly string[]
): string[] {
  const source = keys ?? defaults;
  const values = new Set<string>();
  for (const key of source) {
    if (typeof key !== 'string') continue;
    const normalized = normalizeKey(key);
    if (normalized) {
      values.add(normalized);
    }
  }
  return Array.from(values);
}

function normalizeNonNegativeInteger(value: number | undefined, fallback: number) {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return fallback;
  }
  const normalized = Math.floor(value);
  if (normalized < 0) {
    return fallback;
  }
  return normalized;
}


